/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

// import { createNewCart, getCartFromCookie } from '@msdyn365-commerce/global-state/src/cart-state';
import { BaseCartState, createNewCart, getCartFromCookie, GlobalStateInput, ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import { Cart, CartLine } from '@msdyn365-commerce/retail-proxy';
import { computed } from 'mobx';
import { IWineClub, wineClubSetup } from '../../settings/app.settings';
import { CartUtilities } from '../../Utilities/cart-utils';
import { BusLoader, LoadBus } from '../../Utilities/event-bus';
import { WineClubDataStore } from '../wine-club-signup/wine-club-data';
import { CartDataCookie } from './cart-data-cookie';
import { CartVerifier } from './cart-verified';
import { ICartmanagerData } from './cartmanager.data';
import { cartType, ICartmanagerProps } from './cartmanager.props.autogenerated';

export interface ICartmanagerViewProps extends ICartmanagerProps<ICartmanagerData> {

}

function printC(log: string, color: string): void {
    console.log(`%c${log}`, `color: ${color}`);
}

// WINE CART VALIDATION DOES NOT DEFAULT
/**
 *
 * Cartmanager component
 * @extends {React.PureComponent<ICartmanagerProps<ICartmanagerData>>}
 */
@LoadBus('cartmanager')
class Cartmanager extends React.PureComponent<ICartmanagerProps<ICartmanagerData>> {

    // TODO: to remove after testing
    private CART_TYPE: cartType = cartType.CLUB;

    private bus!: BusLoader;

    // declaration on what sort of data persistence behaviour will be done for
    // each cart type
    private readonly PERSISTENCE_MAP: { [cartDataType: string]: boolean } = {
        [cartType.WINE]: true,
        [cartType.CLUB]: false
    };

    @computed private get _clubItemIds(): string[] {
        const wineClub = this.props.app.config.wineClubSetup as wineClubSetup;
        const ids: string[] = [];
        wineClub.forEach(club => club.variants.forEach(variant => ids.push(variant.itemId)));
        return ids;
    }

    public constructor(props: ICartmanagerProps<ICartmanagerData>) {
        super(props);

        // TODO: when done testing, remove the class field and replace
        // TODO: fully with props
        this.CART_TYPE = this.props.config.cartType;
    }

    public componentDidMount(): void {
        void this._processCartManager();
    }

    public render(): JSX.Element | null {
        return this.props.renderView(this.props);
    }

    private async _processCartManager(): Promise<void> {
        printC('ENTER CART MANAGER----------------------------------------------', 'red');
        const doCartRepair = await this._doesCartNeedRepair();
        printC(`CART REPAIR CHECK FINISHED WITH : ${doCartRepair}`, 'red');
        if (doCartRepair) {
            await this._repairCartAndProcess();
            await this._autoSetAnonCartCookie();
        } else {
            await this._autoSetAnonCartCookie();
            await this._processSwappableCart();
        }
        printC('PROCESS CART OPERATION COMPLETE', 'red');
        await this._preserveCartSplit();
        const query = this.props.context.request.query;
        // tslint:disable-next-line: no-typeof-undefined
        if (this.props.config.redirectPage?.destinationUrl && typeof window !== 'undefined') {
            printC(`REDIRECTING TO ${this.props.config.redirectPage.destinationUrl}`, 'red');
            let destinationUrl = this.props.config.redirectPage.destinationUrl;
            const refId = sessionStorage.getItem('refId');
            if(refId) {
                destinationUrl = destinationUrl.includes('?') ? `${destinationUrl}&refId=${refId}`: `${destinationUrl}?refId=${refId}`;
                sessionStorage.removeItem('refId');
            } else if(query?.refId) {
                destinationUrl = destinationUrl.includes('?') ? `${destinationUrl}&refId=${query.refId}`: `${destinationUrl}?refId=${query.refId}`;
            }
            window.location.href = destinationUrl;

            // tslint:disable-next-line: no-typeof-undefined
        } else if (doCartRepair && typeof window !== 'undefined') {
            printC('RELOADING PAGE', 'red');
            window.location.reload();
        }
    }

    private async _repairCartAndProcess(): Promise<void> {
        printC('ENTER REPAIR CART----------------------------------------------', 'blue');
        const cartState = await this.props.data.cart;
        const lines = cartState.cart.CartLines || [];
        const sortedItems = this._sortCart(lines);
        printC(`SORTED ITEMS: ${JSON.stringify(sortedItems)}`, 'blue');
        const itemsForAdd = {
            club: sortedItems.club.map(item => { return { id: item.ProductId!, amount: item.Quantity || 1 }; }),
            nonclub: sortedItems.nonclub.map(item => { return { id: item.ProductId!, amount: item.Quantity || 1 }; })
        };

        // we do the reverse based on the page's cart type
        if (this.CART_TYPE === cartType.CLUB) {
            printC('CART TYPE CLUB DETECTED', 'blue');
            const activeClubId = this._getClubItemId();

            // remove ALL items, we only want club in this cart
            await this._removeCartItems([...sortedItems.club, ...sortedItems.nonclub]);
            await this._addCartItems([{ id: +activeClubId, amount: 1 }]);

            const clubCartCookie = new CartDataCookie(
                this.props.context.request.cookies
            );

            clubCartCookie.cartId = this.props.context.request.cookies.getCartCookie();
            clubCartCookie.cookieType = cartType.CLUB;
            clubCartCookie.save();

            // create a new cart in the wine context
            await this._processNewCart(cartType.WINE);

            // now within the wine's context, re-add the nonclub items
            await this._addCartItems(itemsForAdd.nonclub);

            await this._processCartSwap(clubCartCookie);
        }

        // same as above
        if (this.CART_TYPE === cartType.WINE) {
            printC('CART TYPE CLUB WINE', 'blue');
            await this._removeCartItems(sortedItems.club);

            new CartDataCookie(
                this.props.context.request.cookies
            ).clear(cartType.CLUB);
        }
    }

    /**
     * main swap process
     */
    private async _processSwappableCart(): Promise<void> {
        printC('ENTER SWAPPABLE CART--------------------------------------------', 'green');
        // on every page load, ensure the proper cart is being used
        const cart = await this.props.data.cart;

        // construct a cart cookie and attempt a load of the cart id
        const cookie = new CartDataCookie(this.props.context.request.cookies);

        // if the load was successful and the cookie contains a valid cart id
        // (not undefined), then initiate a check to ensure
        if (cookie.load(this.CART_TYPE) && cookie.cartId) {
            printC(`COOKIE ${cookie.cartId} of ${this.CART_TYPE} found`, 'green');

            // if the current cart DOES NOT match the cart that should be loaded
            // on this page
            if (cookie.cartId.split(':')[1] !== cart.cart.Id) {
                printC(`CART ID'S DO NOT MATCH, PROCEED WITH SWAP`, 'green');
                // depending on wether we want the cart types data to stay,
                // create a new cart or wipe and delete
                if (this.PERSISTENCE_MAP[this.CART_TYPE]) {
                    printC(`SWAPPING CURRENT CART WITH COOKIE ${JSON.stringify(cookie)}`, 'green');
                    await this._processCartSwap(cookie);
                } else {
                    printC(`CREATING NEW CART OF TYPE ${this.CART_TYPE}`, 'green');
                    await this._processNewCart(this.CART_TYPE);
                }
            }
        } else {
            printC('EXISTING COOKIE NOT FOUND', 'green');
            printC(`CREATING NEW CART OF TYPE ${this.CART_TYPE}`, 'green');
            await this._processNewCart(this.CART_TYPE);
        }

        // cache as a promise maybe?
        CartVerifier.instance.isVerified = true;
        this.bus.publish('cart-verified');
        // for debugging which cart is currently active
        // console.log(JSON.parse(JSON.stringify(cart.cart.CartLines)));
    }

    /**
     * swaps the cart to another specified cart via a cart data cookie
     *
     * @param toCookie cart cookie to swap to
     * @returns the cart or a failure
     */
    private async _processCartSwap(toCookie: CartDataCookie): Promise<Cart | undefined> {
        printC('CART SWAPING NOW', 'blue');
        // this will AUTOMATICALLY set the cookie in storage, no other
        // processing needed
        const newCart = await getCartFromCookie(
            toCookie.cartId!.split(':'),
            this.props.context.actionContext
        );

        await this._triggerCartReload();

        return newCart;
    }

    /**
     * create a fresh new cart
     */
    private async _processNewCart(cookieCartType: cartType): Promise<Cart | undefined> {
        const newCart = await this._createCart();

        // if the new cart was successfully created, create a cookie to match up
        // with the new cart
        if (newCart) {
            const newCartCookie = new CartDataCookie(
                this.props.context.request.cookies
            );
            newCartCookie.cartId = this.props.context.request.cookies.getCartCookie();
            newCartCookie.cookieType = cookieCartType;
            newCartCookie.save();

        }

        await this._triggerCartReload();
        return newCart || undefined;
    }

    /**
     * creates a fresh and new empty cart
     *
     * @returns cart id
     */
    private async _createCart(): Promise<Cart | void> {
        // this will AUTOMATICALLY set the cookie in storage, no other
        // processing needed
        const newCart = await createNewCart(this.props.context.actionContext);
        if (newCart) {
            return newCart;
        } else { console.error('[CartManager] failed new cart load'); }
    }

    private async _triggerCartReload(): Promise<void> {
        const cart = await this.props.data.cart;

        // IMPORTANT - this line modified a private variable within the
        // cart's source code. this allows us to reinitialise the cart
        // with the correct carts changes without refreshing the page
        // @ts-ignore
        cart.isInitialized = false;
        await cart.initialize();

        // taken from addtocart.component.tsx:updateCart()
        setTimeout(async () => {
            const context = this.props.context;
            const cartInput = new GlobalStateInput<ICartState>('CARTSTATE', BaseCartState, context.actionContext.requestContext.apiSettings);
            context.actionContext.update(cartInput, cart);
            await cart.refreshCart({});

            // tslint:disable-next-line: no-typeof-undefined
            if (typeof window !== 'undefined') {
                const curUrl = new URL(window.location.href);

                if (
                    this.props.config.refreshOnAction &&
                    !this.props.config.redirectPage?.destinationUrl &&
                    !curUrl.searchParams.has('mg_refresh')
                ) {
                    printC('REFRESHING PAGE', 'BLACK');
                    curUrl.searchParams.set('mg_refresh', 'true');
                    window.location.href = curUrl.href;
                }
            }
        }, 10);
    }

    /**
     * checks if theres a mix of club items and not
     *
     * @returns if cart need a repair
     */
    private async _doesCartNeedRepair(): Promise<boolean> {
        const cartState = await this.props.data.cart;
        const lines = cartState.cart.CartLines || [];

        const clubCookie = new CartDataCookie(this.props.context.request.cookies);
        const wineCookie = new CartDataCookie(this.props.context.request.cookies);
        clubCookie.load(cartType.CLUB);
        wineCookie.load(cartType.WINE);

        // we need at least one of these to exist to test. if both are undefined
        // it will pass the test and cause a cart repair where its not needed.
        // (will infinite refresh loop new customers)
        if (clubCookie.cartId !== undefined || wineCookie.cartId !== undefined) {
            if (clubCookie.cartId === wineCookie.cartId) {
                return true;
            }
        }

        let hasClubItem: boolean = false;
        let hasWineItem: boolean = false;

        for (const line of lines) {
            if (hasClubItem && hasWineItem) { break; }
            const isClubItem = this._clubItemIds.find(itemId => +itemId === line.ProductId);
            if (isClubItem) { hasClubItem = true; } else { hasWineItem = true; }
        }

        // no item exception
        if (!hasClubItem && !hasWineItem) { return false; }

        // if cart has more than 1 club item
        if (hasClubItem && lines.length !== 1) { return true; }
        if (lines.length === 0) { return true; }

        // if is cart mixed logic

        // true, false => false
        // false, true => false
        // true, true => true
        return !(hasWineItem && !hasClubItem || !hasWineItem && hasClubItem);
    }

    /**
     * sorts cart based on club / nonclub items
     *
     * @param lines cart lines to sort
     * @returns sorted lines
     */
    private _sortCart(lines: CartLine[]): { club: CartLine[]; nonclub: CartLine[] } {
        const nonclub: CartLine[] = [];
        const club: CartLine[] = [];
        for (const line of lines) {
            const hasItem = this._clubItemIds.includes(`${line.ProductId}` || '');

            if (!hasItem) {
                nonclub.push(line);
            } else {
                club.push(line);
            }
        }
        return { club, nonclub };
    }

    /**
     * removes items from the cart based by line
     *
     * @param items items to remove
     * @returns the result of removal
     */
    private async _removeCartItems(items: CartLine[]): Promise<ICartActionResult> {
        const lineIds = items.map(item => item.LineId!);

        const cartState = await this.props.data.cart;
        return cartState.removeCartLines({
            cartLineIds: lineIds
        });
    }

    private async _addCartItems(items: { id: number; amount: number }[]): Promise<void> {
        for (const item of items) {
            await CartUtilities.addItem({
                context: this.props.context,
                recordId: item.id,
                amount: item.amount
            }, true);
        }
    }

    private async _isValidClubCart(): Promise<boolean> {
        const cartState = await this.props.data.cart;
        const lines = cartState.cart.CartLines || [];
        if (lines.length === 0) { return false; } // empty carts are not valid
        for (const line of lines) {
            const isClubItem = this._clubItemIds.find(id => +id === line.ProductId);
            if (!isClubItem) { return false; }
        }
        return true;
    }

    private async _isValidWineCart(): Promise<boolean> {
        const cartState = await this.props.data.cart;
        const lines = cartState.cart.CartLines || [];
        if (lines.length === 0) { return false; } // empty carts are not valid
        for (const line of lines) {
            const isClubItem = this._clubItemIds.find(id => +id === line.ProductId);
            if (isClubItem) { return false; }
        }
        return true;
    }

    /**
     * take an anon cart cookie, possibly from a cart merge and set it as a
     * proper cart save
     *
     * why do this? for some reason if we dont take cart ownership here, the
     * anon carts items dont persist and get removed. this is essentially used
     * as a prevalidation check to make sure our cart cookies match post cart
     * merge (the cart cookie gets a new id when you merge)
     */
    private async _autoSetAnonCartCookie(): Promise<void> {
        printC(`ENTER AUTOSET ANON CART COOKIE----------------------------------`, 'green');
        const isClubCart = await this._isValidClubCart();
        const isWineCart = await this._isValidWineCart();
        printC(`VALID CLUB CART IS ${isClubCart}`, 'green');
        printC(`VALID WINE CART IS ${isWineCart}`, 'green');

        const cookie = new CartDataCookie(this.props.context.request.cookies);
        if (isClubCart) {
            printC('SET CUR CART COOKIE AS CLUB', 'green');
            cookie.cartId = this.props.context.request.cookies.getCartCookie();
            cookie.cookieType = cartType.CLUB;
            cookie.save();
        } else if (isWineCart) {
            printC('SET CUR CART COOKIE AS WINE', 'green');
            cookie.cartId = this.props.context.request.cookies.getCartCookie();
            cookie.cookieType = cartType.WINE;
            cookie.save();
        }
    }

    private _getClubItemId(): string {
        const wineClubs = this.props.app.config.wineClubSetup as IWineClub[] || [];
        const store = WineClubDataStore.getInstance().get();
        const club = wineClubs[store.clubId!];
        if (!club) {
            return '';
        }
        const variant = club.variants.find(cVariant => cVariant.winePreference === store.variantPreference);

        if (!variant) { return ''; }
        printC(`[_getClubItemId] retrieved ${variant.itemId} as club item`, 'orange');
        return variant.itemId;
    }

    private async _preserveCartSplit(): Promise<void> {
        printC('ENTER PRESERVE CART SPLIT---------------------------------------', 'blue');
        const cartState = await this.props.data.cart;

        const lines = cartState.cart.CartLines || [];

        printC(`PRESERVING SPLIT AS ${this.CART_TYPE}`, 'blue');
        if (this.CART_TYPE === cartType.WINE) {
            const foundClubItems = lines.filter(line =>
                this._clubItemIds.find(id => +id === line.ProductId)
            );

            if (foundClubItems.length !== 0) {
                await this._removeCartItems(foundClubItems);
            }
        }

        if (this.CART_TYPE === cartType.CLUB) {
            const foundNonClubItems = lines.filter(line =>
                !this._clubItemIds.find(id => +id === line.ProductId)
            );

            // unfortunately at this point, we have to delete their previous
            // item selections to ensure that they are able to checkout their
            // club
            if (foundNonClubItems) {
                await this._removeCartItems(foundNonClubItems);
            }
        }
    }
}

export default Cartmanager;